import { Card, Color, Shape, Number, Shading } from '../types/game';

const colors: Color[] = ['crimson', 'royalblue', 'forestgreen'];
const shapes: Shape[] = ['triangle', 'circle', 'rectangle'];
const numbers: Number[] = [1, 2, 3];
const shadings: Shading[] = ['solid', 'dotted', 'crosshatched'];

export const generateDeck = (): Card[] => {
    const deck: Card[] = [];
    
    colors.forEach(color => {
        shapes.forEach(shape => {
            numbers.forEach(number => {
                shadings.forEach(shading => {
                    deck.push({
                        id: `${color}-${shape}-${number}-${shading}`,
                        color,
                        shape,
                        number,
                        shading
                    });
                });
            });
        });
    });

    return shuffleDeck(deck);
};

export const shuffleDeck = (deck: Card[]): Card[] => {
    const newDeck = [...deck];
    for (let i = newDeck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
    }
    return newDeck;
};

export const isValidSet = (cards: Card[]): boolean => {
    if (!cards || cards.length !== 3 || !cards[0] || !cards[1] || !cards[2]) return false;

    const [card1, card2, card3] = cards;

    // For each attribute, they must be either all the same or all different
    const colorValid = checkAttribute(card1.color, card2.color, card3.color);
    const shapeValid = checkAttribute(card1.shape, card2.shape, card3.shape);
    const numberValid = checkAttribute(card1.number, card2.number, card3.number);
    const shadingValid = checkAttribute(card1.shading, card2.shading, card3.shading);

    return colorValid && shapeValid && numberValid && shadingValid;
};

const checkAttribute = <T>(a: T, b: T, c: T): boolean => {
    // All same
    if (a === b && b === c) return true;
    // All different
    if (a !== b && b !== c && a !== c) return true;
    return false;
};

export const dealCards = (deck: Card[], count: number = 12, existingCards: Card[] = []): [Card[], Card[]] => {
    let dealt: Card[] = [];
    let remaining = [...deck];
    let attempts = 0;
    const maxAttempts = 10;  // Prevent infinite loops

    do {
        // Reset for new attempt
        dealt = remaining.slice(0, count);
        remaining = remaining.slice(count);

        // Check if there's at least one set in the combined cards
        const allCards = [...existingCards, ...dealt];
        
        // If we found at least one set, or this isn't a full board deal, or we've hit max attempts
        if (findAllSets(allCards).length > 0 || existingCards.length === 0 || attempts >= maxAttempts) {
            break;
        }

        // If no sets found, shuffle remaining cards and try again
        remaining = shuffleDeck([...dealt, ...remaining]);
        attempts++;
    } while (attempts < maxAttempts);

    return [dealt, remaining];
};

export const findAllSets = (cards: Card[]): Card[][] => {
    if (!cards || cards.length < 3) return [];
    
    const sets: Card[][] = [];
    for (let i = 0; i < cards.length - 2; i++) {
        if (!cards[i]) continue;
        for (let j = i + 1; j < cards.length - 1; j++) {
            if (!cards[j]) continue;
            for (let k = j + 1; k < cards.length; k++) {
                if (!cards[k]) continue;
                const potentialSet = [cards[i], cards[j], cards[k]];
                if (isValidSet(potentialSet)) {
                    sets.push(potentialSet);
                }
            }
        }
    }
    return sets;
};

export const generateDailyChallengeBoard = (): Card[] => {
    let deck = generateDeck();
    let tempCardsInPlay: Card[] = [];
    let attempts = 0;
    
    while (findAllSets(tempCardsInPlay).length !== 6 && attempts < 1000) {
        deck = generateDeck();
        [tempCardsInPlay] = dealCards(deck, 12); // Deal 12 cards
        attempts++;
    }

    if (findAllSets(tempCardsInPlay).length === 6) {
        return tempCardsInPlay;
    } else {
        console.error('Failed to generate a board with exactly 6 sets.');
        return [];
    }
}; 